import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Container,
  styled,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import { ResponsivePie } from '@nivo/pie';

// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

// Define the StyledPaper component
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

// Utility functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

// Custom tooltip component for charts
const CustomTooltip = ({ title, value, percentage }) => {
  return (
    <Box
      sx={{
        padding: 1.5,
        backgroundColor: 'white',
        borderRadius: 1,
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Typography variant="body2">Amount: {value}</Typography>
      <Typography variant="body2">Allocation: {percentage}</Typography>
    </Box>
  );
};

// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

const RealEstateDirect = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-real-estate-real-estate-direct', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyTransactions, setPropertyTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editPrice, setEditPrice] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  
  // Form state
  const [propertyName, setPropertyName] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [price, setPrice] = useState('');
  
  // User's saved property names (will be populated from existing investments)
  const [userPropertyNames, setUserPropertyNames] = useState([]);

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Fetching direct real estate investments for user ID:', user.id);
      const { data, error } = await supabase
        .from('real_estate_direct_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      console.log('Fetched direct real estate data:', data);
      
      if (data && data.length > 0) {
        // Transform the data to match the expected format for the component
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: item.price,
          purchaseDate: item.purchase_date,
        }));
        
        // Group investments by property name
        const groupedInvestments = groupInvestmentsByProperty(formattedData);
        
        setInvestments(groupedInvestments);
        const total = formattedData.reduce((sum, item) => sum + item.value, 0);
        setTotalValue(total);
        console.log('Total direct real estate value:', total);
        
        // Extract unique property names from user's investments
        const uniquePropertyNames = [...new Set(data.map(item => item.name))];
        setUserPropertyNames(uniquePropertyNames);
      } else {
        console.log('No direct real estate investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserPropertyNames([]);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
      // Fallback to empty data in case of error
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);
  
  // Group investments by property name
  const groupInvestmentsByProperty = (data) => {
    const groupedMap = data.reduce((acc, investment) => {
      if (!acc[investment.name]) {
        acc[investment.name] = {
          name: investment.name,
          totalValue: 0,
          transactions: []
        };
      }
      
      acc[investment.name].totalValue += investment.value;
      acc[investment.name].transactions.push(investment);
      
      return acc;
    }, {});
    
    // Convert the grouped map to an array
    return Object.values(groupedMap).map((property, index) => ({
      id: index + 1,
      name: property.name,
      value: property.totalValue,
      transactions: property.transactions
    }));
  };
  
  // Initial data fetch and connection test
  useEffect(() => {
    const init = async () => {
      try {
        // Test Supabase connection
        console.log('Testing Supabase connection...');
        const { data: connectionTest, error: connectionError } = await supabase
          .from('real_estate_direct_investments')
          .select('count')
          .limit(1);
          
        if (connectionError) {
          console.error('Supabase connection error:', connectionError);
          
          // Check if the error is related to table not existing
          if (connectionError.message && connectionError.message.includes('does not exist')) {
            console.error('The real_estate_direct_investments table does not exist in the database.');
            alert('The direct real estate investments table does not exist. Please run the SQL query to create it.');
          }
        } else {
          console.log('Supabase connection successful:', connectionTest);
          
          // Fetch investments
          await fetchInvestments();
        }
      } catch (error) {
        console.error('Initialization error:', error);
        setLoading(false);
      }
    };
    
    init();
  }, [fetchInvestments]);

  const pieChartData = investments.map((item) => ({
    id: item.id,
    value: item.value,
    label: item.name,
  }));

  const handleOpenModal = () => {
    console.log('Opening Add Investment modal');
    setOpenModal(true);
  };
  
  const handleCloseModal = () => {
    console.log('Closing Add Investment modal');
    setOpenModal(false);
  };

  const handleReset = () => {
    setPropertyName(null);
    setPurchaseDate(new Date());
    setPrice('');
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!propertyName || !purchaseDate || !price) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Get the property name (either from string input or from selected option)
      const propertyNameValue = typeof propertyName === 'string' ? propertyName : propertyName.label || propertyName;
      
      // Prepare the data for Supabase
      const newInvestment = {
        name: propertyNameValue,
        purchase_date: purchaseDate.toISOString().split('T')[0],
        price: parseFloat(price),
        user_id: user.id
      };

      console.log('Creating new investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('real_estate_direct_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Refresh the data from Supabase
      if (data && data.length > 0) {
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransactionHistory = (property) => {
    setSelectedProperty(property);
    setPropertyTransactions(property.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedProperty(null);
    setPropertyTransactions([]);
  };

  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    // Set form values with defaults in case of null/undefined values
    setEditPrice(currentTransaction.value ? currentTransaction.value.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    
    setOpenEditModal(true);
  };

  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };

  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleUpdateTransaction = async () => {
    if (!editPrice || !editPurchaseDate) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare the data for Supabase
      const updatedTransaction = {
        name: currentTransaction.name,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        price: parseFloat(editPrice),
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('real_estate_direct_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedProperty && openHistoryModal) {
        // Get the latest transactions for this property
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('real_estate_direct_investments')
          .select('*')
          .eq('name', selectedProperty.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            value: item.price,
            purchaseDate: item.purchase_date,
          }));
          
          setPropertyTransactions(formattedTransactions);
        }
      }

      // Close the edit modal
      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('real_estate_direct_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedProperty && openHistoryModal) {
        // Get the latest transactions for this property
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('real_estate_direct_investments')
          .select('*')
          .eq('name', selectedProperty.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              value: item.price,
              purchaseDate: item.purchase_date,
            }));
            
            setPropertyTransactions(formattedTransactions);
          } else {
            // No transactions left for this property, close the modal
            handleCloseHistoryModal();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = (a.value / totalValue) * 100;
        const bAllocation = (b.value / totalValue) * 100;
        return direction === 'asc'
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      }
      return 0;
    });

    setInvestments(sortedData);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/investments/real-estate')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-tour="inv-prod-summary">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Direct Real Estate
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Total: {formatCurrency(totalValue)}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={fetchInvestments} 
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
              }}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button 
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      {/* Add Investment Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Real Estate Investment
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <TextField
                label="Property Name"
                fullWidth
                required
                value={propertyName}
                onChange={(e) => setPropertyName(e.target.value)}
                placeholder="Enter property name"
                helperText="Enter the name of the property"
              />
              
              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">No Direct Real Estate Found</Typography>
          <Typography variant="body2">
            No direct real estate investments found. Start investing to see them here.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Property Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          userSelect: 'none'
                        }}
                      >
                        Property Name
                        {sortConfig.key === 'name' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('value')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          userSelect: 'none'
                        }}
                      >
                        Value
                        {sortConfig.key === 'value' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right" 
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          userSelect: 'none'
                        }}
                      >
                        Allocation
                        {sortConfig.key === 'allocation' && (
                          <span style={{ marginLeft: 8 }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => (
                      <TableRow
                        key={investment.id}
                        onClick={() => handleOpenTransactionHistory(investment)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2,
                              }}
                            />
                            {investment.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercentage(investment.value / totalValue)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View transactions">
                            <IconButton size="small" sx={{ color: 'primary.main' }}>
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Paper
              sx={{
                height: '100%',
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Distribution
                </Typography>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                {pieChartData.length > 0 && (
                  <PieChart colors={chartPalette}
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                    height={450}
                    legend={{
                      hidden: true
                    }}
                    margin={{ top: 10, bottom: 50, left: 20, right: 20 }}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'bottom', horizontal: 'middle' },
                        padding: { top: 10, bottom: 0, left: 0, right: 0 },
                        itemMarkWidth: 8,
                        itemMarkHeight: 8,
                        markGap: 5,
                        itemGap: 15,
                        sx: {
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          gap: 2,
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Transaction History Modal */}
      <Modal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 700 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {selectedProperty?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Value: {formatCurrency(selectedProperty?.value || 0)}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseHistoryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Price (₹)</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {propertyTransactions.length > 0 ? (
                  propertyTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.purchaseDate 
                          ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.value)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {propertyTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(propertyTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={handleCloseHistoryModal}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Transaction Modal */}
      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Property: {currentTransaction?.name}
              </Typography>
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Property: {currentTransaction.name}<br />
                Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                Amount: {formatCurrency(currentTransaction.value)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>
      <GuidedTour steps={TOURS['inv-real-estate-real-estate-direct']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default RealEstateDirect;